import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { CheckCircle, XCircle, Trash2, Bell, BellOff } from 'lucide-react';
import { getDemoRequests, approveDemoRequest, rejectDemoRequest, deleteDemoRequest, getDemoNotificationSettings, updateDemoNotificationSettings } from '../../services/api';
import { toast } from 'sonner';

export const AdminDemosPanel = () => {
  const [demoRequests, setDemoRequests] = useState([]);
  const [notifEnabled, setNotifEnabled] = useState(false);
  const [notifPhone, setNotifPhone] = useState('');
  const [notifSaving, setNotifSaving] = useState(false);

  useEffect(() => {
    loadDemos();
    getDemoNotificationSettings().then(r => {
      setNotifEnabled(r.data.enabled);
      setNotifPhone(r.data.phone || '');
    }).catch(() => { /* ignore */ });
  }, []);

  const loadDemos = async () => {
    try { const res = await getDemoRequests(); setDemoRequests(res.data); } catch {}
  };

  const handleSaveNotif = async () => {
    setNotifSaving(true);
    try {
      await updateDemoNotificationSettings({ enabled: notifEnabled, phone: notifPhone });
      toast.success('Bildirim ayarları kaydedildi');
    } catch { toast.error('Kaydetme hatası'); }
    finally { setNotifSaving(false); }
  };

  const handleToggle = async () => {
    const newVal = !notifEnabled;
    setNotifEnabled(newVal);
    if (!newVal) {
      try {
        await updateDemoNotificationSettings({ enabled: false, phone: notifPhone });
        toast.success('Bildirimler kapatıldı');
      } catch { toast.error('Hata'); }
    }
  };

  return (
    <div className="space-y-4">
      {/* Notification Settings */}
      <div className="bg-white rounded-2xl p-4 shadow-sm">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Bell size={16} className="text-[#6b6de3]" />
            <span className="text-sm font-semibold text-gray-900">Demo Taleplerini Bildir</span>
          </div>
          <button onClick={handleToggle} className="transition-colors" data-testid="demo-notif-toggle">
            {notifEnabled
              ? <span className="flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-full bg-green-100 text-green-600"><Bell size={12} /> Açık</span>
              : <span className="flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-full bg-gray-100 text-gray-500"><BellOff size={12} /> Kapalı</span>}
          </button>
        </div>
        {notifEnabled && (
          <div className="flex gap-2">
            <Input
              value={notifPhone}
              onChange={e => setNotifPhone(e.target.value)}
              placeholder="Bildirim gönderilecek tel no (05XX...)"
              className="h-10 text-sm bg-[#F2F2F7] border-0 rounded-xl flex-1"
              data-testid="demo-notif-phone"
            />
            <Button onClick={handleSaveNotif} disabled={notifSaving} className="bg-[#6b6de3] hover:bg-[#5658c4] rounded-xl h-10 px-4 text-sm" data-testid="demo-notif-save">
              {notifSaving ? '...' : 'Kaydet'}
            </Button>
          </div>
        )}
        {notifEnabled && <p className="text-[10px] text-[#8E8E93] mt-2">Yeni demo talebi geldiğinde bu numaraya SMS gönderilir.</p>}
      </div>

      {/* Demo Requests List */}
      {demoRequests.length > 0 ? demoRequests.map(d => (
        <div key={d.id} className="bg-white rounded-2xl p-4 shadow-sm" data-testid={`demo-${d.id}`}>
          <div className="flex items-start justify-between mb-2">
            <div>
              <p className="font-semibold text-sm text-gray-900">{d.clinic_name}</p>
              <p className="text-xs text-[#8E8E93]">{d.owner_name || d.name} &bull; {d.phone}{d.city ? ` &bull; ${d.city}` : ''}</p>
            </div>
            <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${d.status === 'approved' ? 'bg-green-100 text-green-600' : d.status === 'rejected' ? 'bg-red-100 text-red-600' : 'bg-yellow-100 text-yellow-600'}`}>
              {d.status === 'approved' ? 'Onaylandı' : d.status === 'rejected' ? 'Reddedildi' : 'Bekliyor'}
            </span>
          </div>
          {d.status === 'pending' && (
            <div className="flex gap-2 mt-2">
              <Button size="sm" onClick={() => approveDemoRequest(d.id).then(() => { toast.success('Onaylandı'); loadDemos(); })} className="bg-[#34C759] rounded-full text-xs flex-1" data-testid={`approve-demo-${d.id}`}><CheckCircle size={14} className="mr-1" /> Onayla</Button>
              <Button size="sm" variant="outline" onClick={() => rejectDemoRequest(d.id).then(() => { toast.success('Reddedildi'); loadDemos(); })} className="rounded-full text-xs flex-1 text-[#FF3B30] border-[#FF3B30]" data-testid={`reject-demo-${d.id}`}><XCircle size={14} className="mr-1" /> Reddet</Button>
            </div>
          )}
          {d.status !== 'pending' && (
            <div className="flex justify-end mt-2">
              <button onClick={() => { if (window.confirm('Bu talebi silmek istediğinize emin misiniz?')) deleteDemoRequest(d.id).then(() => { toast.success('Silindi'); loadDemos(); }).catch(() => toast.error('Silinemedi')); }}
                className="p-2 rounded-lg hover:bg-gray-100 text-[#FF3B30]" data-testid={`delete-demo-${d.id}`}><Trash2 size={15} /></button>
            </div>
          )}
        </div>
      )) : <div className="bg-white rounded-2xl p-8 text-center"><p className="text-[#8E8E93]">Demo talebi yok</p></div>}
    </div>
  );
};
